import { EVENTS } from '@archetype-themes/utils/events'

class BlockBuyButtons extends HTMLElement {
  constructor() {
    super()

    this.handleVariantChange = this.handleVariantChange.bind(this)
  }
  connectedCallback() {
    this.abortController = new AbortController()

    this.addEventListener('submit', this.handleSubmit.bind(this), {
      signal: this.abortController.signal
    })

    document.addEventListener(
      `${EVENTS.variantChange}:${this.dataset.sectionId}:${this.dataset.productId}`,
      this.handleVariantChange,
      {
        signal: this.abortController.signal
      }
    )

    this.cartType = this.dataset.cartType
    
    // Initialize sticky add to cart functionality
    if (this.dataset.showStickyAddToCart === 'true') {
      this.initStickyAddToCart()
    }
  }

  disconnectedCallback() {
    this.abortController.abort()
    
    // Clean up intersection observer
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
    }
  }

  handleVariantChange({ detail }) {
    const { html, variant } = detail

    if (!variant) {
      this.toggleAddButton(true, this.getLocales().unavailable)
      this.toggleStickyButton(true, this.getLocales().unavailable)
      return
    }

    this.updateVariantInput(variant)
    this.renderProductInfo(html)
    this.updateStickyCartInfo(variant)
  }

  renderProductInfo(html) {
    const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`)

    if (addButtonUpdated) {
      const isDisabled = addButtonUpdated.hasAttribute('disabled')
      const buttonText = this.getLocales().soldOut
      this.toggleAddButton(isDisabled, buttonText)
      this.toggleStickyButton(isDisabled, buttonText)
    }
  }

  getLocales() {
    this.locales = this.locales || JSON.parse(this.querySelector('[type="application/json"]').textContent)
    return this.locales
  }

  toggleAddButton(disable = true, text) {
    const productForm = this.querySelector(`#product-form-${this.dataset.sectionId}`)

    if (!productForm) return

    const addButton = productForm.querySelector('[name="add"]')
    const addButtonText = productForm.querySelector('[name="add"] > span')

    if (!addButton) return

    if (disable) {
      addButton.setAttribute('disabled', 'disabled')
      if (text) addButtonText.textContent = text
    } else {
      addButton.removeAttribute('disabled')
      addButtonText.textContent =
        this.dataset.template !== 'preorder' ? this.getLocales().addToCart : this.getLocales().preOrder
    }
  }

  updateVariantInput(variant) {
    const productForms = this.querySelectorAll(
      `#product-form-${this.dataset.sectionId}, #product-form-installment-${this.dataset.sectionId}`
    )

    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]')
      input.value = variant.id

      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  async handleSubmit(event) {
    if (this.cartType == 'page') return

    event.preventDefault()
    this.disableAddToCartButton()

    try {
      const responseJson = await this.addVariantToCart()

      this.dispatchEvent(
        new CustomEvent(EVENTS.ajaxProductAdded, {
          bubbles: true,
          detail: {
            product: responseJson,
            addToCartBtn: this.querySelector(`#ProductSubmitButton-${this.dataset.sectionId}`)
          }
        })
      )
    } catch (error) {
      this.handleError(error)
    } finally {
      this.enableAddToCartButton()
    }
  }

  handleError(error) {
    if (!error.description) {
      console.warn(error)
      return
    }

    let form = this.querySelector('form')
    let errors = this.querySelector('form .errors')

    if (errors) errors.remove()

    let errorDiv = document.createElement('div')
    errorDiv.classList.add('errors', 'text-center')

    if (typeof error.description === 'object') {
      errorDiv.textContent = error.message
    } else {
      errorDiv.textContent = error.description
    }

    form.append(errorDiv)

    this.dispatchEvent(
      new CustomEvent(EVENTS.ajaxProductError, {
        bubbles: true,
        detail: {
          errorMessage: error.description
        }
      })
    )
  }

  async addVariantToCart() {
    const formData = this.getFormDataWithSections()

    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    })

    if (!response.ok) {
      throw await response.json()
    }

    return response.json()
  }

  async fetchCart() {
    return (await fetch(`${window.Shopify.routes.root}cart.js`)).json()
  }

  getFormDataWithSections() {
    const productForm = this.querySelector(`#product-form-${this.dataset.sectionId}`)
    const formData = new FormData(productForm)

    formData.set('sections_url', `${window.Shopify.routes.root}variants/${productForm.id.value}`)
    // Bundled section rendering
    formData.set('sections', 'cart-ajax')

    return formData
  }

  enableAddToCartButton() {
    const productForm = this.querySelector(`#product-form-${this.dataset.sectionId}`)

    if (!productForm) return

    const addButton = productForm.querySelector('[name="add"]')
    addButton.removeAttribute('aria-busy')
    addButton.classList.remove('btn--loading')
  }

  disableAddToCartButton() {
    const productForm = this.querySelector(`#product-form-${this.dataset.sectionId}`)
    const errors = this.querySelector('form .errors')

    if (errors) errors.remove()
    if (!productForm) return

    const addButton = productForm.querySelector('[name="add"]')
    addButton.setAttribute('aria-busy', 'true')
    addButton.classList.add('btn--loading')
  }

  initStickyAddToCart() {
    this.originalAddButton = this.querySelector(`#ProductSubmitButton-${this.dataset.sectionId}`)
    this.stickyCart = this.querySelector('[data-sticky-cart]')
    this.stickyAddButton = this.querySelector('[data-sticky-add-button]')

    if (!this.originalAddButton || !this.stickyCart || !this.stickyAddButton) return

    // Move sticky cart to end of body to avoid stacking context issues
    document.body.appendChild(this.stickyCart)

    // Set up intersection observer to watch the original button
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.hideStickyCart()
          } else {
            this.checkStickyCartVisibility()
          }
        })
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0
      }
    )

    this.intersectionObserver.observe(this.originalAddButton)

    // Set up scroll listener to hide sticky cart near bottom of page
    this.handleScroll = this.handleScroll.bind(this)
    window.addEventListener('scroll', this.handleScroll, {
      signal: this.abortController.signal,
      passive: true
    })

    // Handle sticky button clicks
    this.stickyAddButton.addEventListener('click', this.handleStickyButtonClick.bind(this), {
      signal: this.abortController.signal
    })
  }

  checkStickyCartVisibility() {
    // Check if we're near the bottom of the page
    const distanceFromBottom = document.documentElement.scrollHeight - window.scrollY - window.innerHeight
    const isNearBottom = distanceFromBottom <= 300
    
    if (isNearBottom) {
      this.hideStickyCart()
    } else {
      this.showStickyCart()
    }
  }

  handleScroll() {
    // Only check visibility if original button is not visible
    const buttonRect = this.originalAddButton.getBoundingClientRect()
    const isButtonVisible = buttonRect.top < window.innerHeight && buttonRect.bottom > 0
    
    if (!isButtonVisible) {
      this.checkStickyCartVisibility()
    }
  }

  showStickyCart() {
    if (!this.stickyCart) return
    
    // Force a reflow to ensure the element is ready for transition
    this.stickyCart.offsetHeight
    this.stickyCart.classList.add('sticky-add-to-cart--visible')
    this.adjustLauncherPosition(true)
  }

  hideStickyCart() {
    if (!this.stickyCart) return
    
    this.stickyCart.classList.remove('sticky-add-to-cart--visible')
    this.adjustLauncherPosition(false)
  }

  adjustLauncherPosition(stickyCartVisible) {
    const launcher = document.getElementById('launcher')
    if (!launcher) return

    if (stickyCartVisible) {
      launcher.classList.add('launcher--with-sticky-cart')
    } else {
      // Reset launcher position
      launcher.classList.remove('launcher--with-sticky-cart')
    }
  }

  handleStickyButtonClick() {
    // Trigger the original form submission
    const productForm = this.querySelector(`#product-form-${this.dataset.sectionId}`)
    if (productForm) {
      // Copy quantity from sticky selector to main form if needed
      this.syncQuantitySelectors()
      
      // Trigger the original add to cart button click
      this.originalAddButton.click()
    }
  }

  syncQuantitySelectors() {
    const mainQuantityInput = this.querySelector(`#product-form-${this.dataset.sectionId} input[name="quantity"]`)
    const stickyQuantityInput = this.querySelector('.sticky-add-to-cart input[name="quantity"]')
    
    if (mainQuantityInput && stickyQuantityInput) {
      mainQuantityInput.value = stickyQuantityInput.value
    }
  }

  toggleStickyButton(disable = true, text) {
    if (!this.stickyAddButton) return

    if (disable) {
      this.stickyAddButton.setAttribute('disabled', 'disabled')
      if (text) {
        const buttonText = this.stickyAddButton.querySelector('span')
        if (buttonText) buttonText.textContent = text
      }
    } else {
      this.stickyAddButton.removeAttribute('disabled')
      const buttonText = this.stickyAddButton.querySelector('span')
      if (buttonText) {
        buttonText.textContent = 
          this.dataset.template !== 'preorder' ? this.getLocales().addToCart : this.getLocales().preOrder
      }
    }
  }

  updateStickyCartInfo(variant) {
    if (!this.stickyCart || !variant) return

    // Update price in sticky cart
    const stickyPrice = this.stickyCart.querySelector('.sticky-add-to-cart__price')
    if (stickyPrice) {
      // You may need to fetch updated price HTML or format the price here
      // For now, we'll just update with the variant price
      stickyPrice.innerHTML = `<span class="price">${this.formatPrice(variant.price)}</span>`
    }
  }

  formatPrice(priceInCents) {
    // Basic price formatting - uses Shopify's currency
    const price = priceInCents / 100
    const currency = window.Shopify?.currency?.active || 'USD'
    
    try {
      return new Intl.NumberFormat(navigator.language, {
        style: 'currency',
        currency: currency
      }).format(price)
    } catch (error) {
      // Fallback to USD if currency is not supported
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(price)
    }
  }
}

customElements.define('block-buy-buttons', BlockBuyButtons)
